/**
 * Phone Offset Settings Component
 * Verwaltet die Telefon-Offset-Einstellungen für 3CX Integration
 */

import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  TextField,
  Button,
  Alert,
  Grid,
  Typography,
  Box,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Chip,
} from "@mui/material";
import {
  Phone,
  Schedule,
  Info,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AccessTime,
} from "@mui/icons-material";

import { apiClient } from "../../services/apiClient";
import {
  PhoneOffsets,
  SetPhoneOffsetsRequest,
  SetPhoneOffsetsResponse,
} from "../../types/api-types";
import ThreeCXIntegration from "./ThreeCXIntegration";

interface PhoneOffsetSettingsProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

const PhoneOffsetSettings: React.FC<PhoneOffsetSettingsProps> = ({
  onSuccess,
  onError,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [offsets, setOffsets] = useState<PhoneOffsets>({
    before: null,
    after: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    beforeOffset: "",
    afterOffset: "",
  });
  const [validationErrors, setValidationErrors] = useState({
    beforeOffset: "",
    afterOffset: "",
  });

  // Load current offsets
  const loadOffsets = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await apiClient.getPhoneOffsets();
      setOffsets(data);

      // Populate form data
      setFormData({
        beforeOffset: data.before || "",
        afterOffset: data.after || "",
      });
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        "Fehler beim Laden der Telefon-Offsets";
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOffsets();
  }, []);

  // Validate time format (HH:MM)
  const validateTimeFormat = (time: string): boolean => {
    if (!time) return true; // Empty is allowed
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(time);
  };

  // Handle input changes
  const handleInputChange = (
    field: "beforeOffset" | "afterOffset",
    value: string
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Validate format
    if (value && !validateTimeFormat(value)) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: "Ungültiges Zeitformat (erwartet: HH:MM)",
      }));
    } else {
      setValidationErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  // Handle save
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Final validation
      const hasErrors = Object.values(validationErrors).some(
        (error) => error !== ""
      );
      if (hasErrors) {
        setError("Bitte korrigieren Sie die Eingabefehler");
        return;
      }

      const request: SetPhoneOffsetsRequest = {
        beforeOffset: formData.beforeOffset || null,
        afterOffset: formData.afterOffset || null,
      };

      const response = await apiClient.setPhoneOffsets(request);

      // Update local state
      setOffsets({
        before: formData.beforeOffset || null,
        after: formData.afterOffset || null,
      });

      setIsEditing(false);

      const successMessage = "Telefon-Offsets erfolgreich gespeichert";
      setSuccess(successMessage);

      // Show 3CX sync result
      if (response.data?.threeCXSync) {
        if (response.data.threeCXSync.success) {
          setSuccess(successMessage + " und mit 3CX synchronisiert");
        } else {
          setSuccess(
            successMessage +
              " (3CX Sync fehlgeschlagen: " +
              response.data.threeCXSync.error +
              ")"
          );
        }
      }

      onSuccess?.();
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        "Fehler beim Speichern der Telefon-Offsets";
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setFormData({
      beforeOffset: offsets.before || "",
      afterOffset: offsets.after || "",
    });
    setValidationErrors({ beforeOffset: "", afterOffset: "" });
    setIsEditing(false);
    setError(null);
    setSuccess(null);
  };

  // Handle edit
  const handleEdit = () => {
    setIsEditing(true);
    setError(null);
    setSuccess(null);
  };

  // Format time display
  const formatTimeDisplay = (time: string | null): string => {
    return time || "Nicht gesetzt";
  };

  // Get offset effect description
  const getOffsetEffect = (
    originalTime: string,
    offsetTime: string | null,
    isStartTime: boolean
  ): string => {
    if (!offsetTime) return originalTime;

    const originalMinutes = timeToMinutes(originalTime);
    const offsetMinutes = timeToMinutes(offsetTime);

    if (isStartTime) {
      // Vor-Offset: frühere Zeit
      const resultMinutes = Math.min(originalMinutes, offsetMinutes);
      return minutesToTime(resultMinutes);
    } else {
      // Nach-Offset: spätere Zeit
      const resultMinutes = Math.max(originalMinutes, offsetMinutes);
      return minutesToTime(resultMinutes);
    }
  };

  // Helper functions
  const timeToMinutes = (time: string): number => {
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes;
  };

  const minutesToTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <Card sx={{ borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
        <CardContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={4}
          >
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Lade Telefon-Offset-Einstellungen...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card sx={{ borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
        <CardHeader
          avatar={<Phone color="primary" />}
          title="Telefon Einstellungen"
          action={
            !isEditing && (
              <Button
                variant="outlined"
                startIcon={<Schedule />}
                onClick={handleEdit}
                disabled={saving}
              >
                Bearbeiten
              </Button>
            )
          }
        />

        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Alert severity="info" sx={{ mb: 3 }}>
            <Box>
              <Typography variant="body2" fontWeight="bold">
                <Info sx={{ fontSize: 16, mr: 1, verticalAlign: "middle" }} />
                Funktionsweise der Telefon-Offsets:
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                • <strong>Vor-Offset:</strong> Telefon ist bereits vor der
                offiziellen Öffnung erreichbar
              </Typography>
              <Typography variant="body2">
                • <strong>Nach-Offset:</strong> Telefon bleibt nach der
                offiziellen Schließung erreichbar
              </Typography>
            </Box>
          </Alert>

          <Grid container spacing={3}>
            {/* Current Settings Display */}
            {!isEditing && (
              <>
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      p: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      bgcolor: "background.paper",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                    >
                      <AccessTime sx={{ mr: 1, verticalAlign: "middle" }} />
                      Vor-Offset
                    </Typography>
                    <Chip
                      label={formatTimeDisplay(offsets.before)}
                      color={offsets.before ? "primary" : "default"}
                      variant="outlined"
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Telefon erreichbar ab dieser Uhrzeit
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      p: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      bgcolor: "background.paper",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                    >
                      <AccessTime sx={{ mr: 1, verticalAlign: "middle" }} />
                      Nach-Offset
                    </Typography>
                    <Chip
                      label={formatTimeDisplay(offsets.after)}
                      color={offsets.after ? "primary" : "default"}
                      variant="outlined"
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      {offsets.after
                        ? "Telefon erreichbar bis zu dieser Uhrzeit"
                        : "Telefon erreichbar bis Schließung"}
                    </Typography>
                  </Box>
                </Grid>
              </>
            )}

            {/* Edit Form */}
            {isEditing && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Vor-Offset (HH:MM)"
                    value={formData.beforeOffset}
                    onChange={(e) =>
                      handleInputChange("beforeOffset", e.target.value)
                    }
                    placeholder="z.B. 08:30"
                    error={!!validationErrors.beforeOffset}
                    helperText={
                      validationErrors.beforeOffset ||
                      "Telefon ist ab dieser Uhrzeit erreichbar (leer lassen für keinen Offset)"
                    }
                    InputProps={{
                      startAdornment: (
                        <Schedule sx={{ mr: 1, color: "text.secondary" }} />
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Nach-Offset (HH:MM)"
                    value={formData.afterOffset}
                    onChange={(e) =>
                      handleInputChange("afterOffset", e.target.value)
                    }
                    placeholder="z.B. 18:30"
                    error={!!validationErrors.afterOffset}
                    helperText={
                      validationErrors.afterOffset ||
                      "Telefon bleibt bis zu dieser Uhrzeit erreichbar (leer lassen für keinen Offset)"
                    }
                    InputProps={{
                      startAdornment: (
                        <Schedule sx={{ mr: 1, color: "text.secondary" }} />
                      ),
                    }}
                  />
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 2,
                      mt: 2,
                    }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={handleCancel}
                      disabled={saving}
                    >
                      Abbrechen
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSave}
                      disabled={
                        saving ||
                        Object.values(validationErrors).some((e) => e !== "")
                      }
                    >
                      {saving ? "Speichern..." : "Speichern"}
                    </Button>
                  </Box>
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* 3CX Integration */}
      <ThreeCXIntegration
        title="3CX Telefon-Offset Integration"
        type="phone-offsets"
        onSyncSuccess={() => {
          setSuccess("Telefon-Offsets erfolgreich mit 3CX synchronisiert");
          loadOffsets(); // Reload offsets after sync
        }}
        onSyncError={(error) => {
          setError(`3CX Synchronisation fehlgeschlagen: ${error}`);
          onError?.(error);
        }}
      />
    </>
  );
};

export default PhoneOffsetSettings;
